import { useState } from "react";
import { Phone, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_CATEGORIES } from "@/data/faqCategories";

const FAQSection = () => {
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(1);
  const activeCategory = FAQ_CATEGORIES[selectedCategoryIndex];
  const faqItems = activeCategory.items;

  return (
    <section 
      className="w-full relative overflow-hidden py-16 lg:py-0"
      style={{ 
        minHeight: 1318,
        background: '#27243A'
      }}
    >
      {/* Ambient Ellipse - Bottom Left (Purple) */}
      <div 
        className="absolute rounded-full pointer-events-none blur-[430px]"
        style={{
          width: 474,
          height: 474,
          top: 621,
          left: -128,
          background: '#533DDA',
          opacity: 0.1,
        }}
      />
      
      {/* Ambient Ellipse - Top Right (Yellow/Green) */}
      <div 
        className="absolute rounded-full pointer-events-none blur-[430px]"
        style={{
          width: 474,
          height: 474,
          top: -76,
          left: 1108,
          background: '#EDFF91',
          opacity: 0.1,
        }}
      />
      
      {/* Ambient Ellipse - Middle Right (Yellow/Green) */}
      <div 
        className="absolute rounded-full pointer-events-none blur-[430px]"
        style={{
          width: 474,
          height: 474,
          top: 462,
          left: 1076,
          background: '#EDFF91',
          opacity: 0.1,
        }}
      />

      <div className="container mx-auto px-4 lg:px-0 relative">
        {/* Badge - Frequently Asked Questions */}
        <div 
          className="flex items-center justify-center rounded-[10px] mx-auto lg:mx-0 lg:absolute"
          style={{
            width: 266,
            height: 38,
            top: 88,
            left: 80,
            background: '#4833C8'
          }}
        >
          <span 
            className="font-inter font-semibold text-white text-center"
            style={{
              fontSize: 18,
              lineHeight: '57px',
              letterSpacing: '-0.05em'
            }}
          >
            Frequently Asked Questions
          </span>
        </div>

        {/* Headline */}
        <h2 
          className="font-display font-semibold text-center lg:text-left mt-6 lg:mt-0 lg:absolute"
          style={{
            width: '100%',
            maxWidth: 784,
            top: 162,
            left: 80,
            fontSize: 'clamp(36px, 5vw, 64px)',
            lineHeight: '1.1',
            letterSpacing: '-0.05em',
            background: 'linear-gradient(90deg, #FFFFFF 0%, #999999 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Frequently Asked Questions
        </h2>

        {/* Mobile/Tablet Layout */}
        <div className="lg:hidden mt-10 space-y-6">
          {/* FAQ Accordion Card */}
          <div 
            className="rounded-2xl overflow-hidden p-6"
            style={{
              background: 'rgba(164, 164, 164, 0.15)',
            }}
          >
            <h3 
              className="font-display font-semibold mb-6"
              style={{
                fontSize: 28,
                lineHeight: '1.2',
                letterSpacing: '-0.05em',
                background: 'linear-gradient(90deg, #FFFFFF 0%, #999999 68.68%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              How do I make the switch to Perch Mobile?
            </h3>
            
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item) => (
                <AccordionItem 
                  key={item.id} 
                  value={item.id}
                  className="border-b border-white/20 last:border-b-0"
                >
                  <AccordionTrigger className="py-4 hover:no-underline group [&>svg]:hidden">
                    <span 
                      className="font-display font-semibold text-white text-left pr-4"
                      style={{
                        fontSize: 18,
                        lineHeight: '1.4',
                        letterSpacing: '-0.05em',
                      }}
                    >
                      {item.question}
                    </span>
                    <span className="shrink-0">
                      <ChevronDown 
                        className="h-6 w-6 transition-transform duration-200 group-data-[state=open]:rotate-180" 
                        style={{ color: '#B0F18D' }}
                      />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 text-white/70 font-inter text-base leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Still Have Questions Card */}
          <div 
            className="rounded-2xl overflow-hidden p-6"
            style={{ background: '#533DDA' }}
          >
            <h3 
              className="font-display font-semibold mb-4"
              style={{
                fontSize: 28,
                lineHeight: '1.2',
                letterSpacing: '-0.05em',
                background: 'linear-gradient(90deg, #FFFFFF 0%, #999999 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Still Have Questions?
            </h3>
            <p 
              className="font-inter font-medium mb-6"
              style={{
                fontSize: 16,
                lineHeight: '1.5',
                color: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              Our Team Is Here To Help. Contact Us By Phone, Email, Or Visit Our Support Page For Quick Answers.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-lg font-inter font-semibold px-6 py-3"
              style={{
                background: '#B0F18D',
                color: '#000000',
                fontSize: 16,
              }}
            >
              <Phone size={18} />
              Contact Us
            </Link>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          {/* FAQ Category Card */}
          <div 
            className="absolute rounded-2xl overflow-hidden"
            style={{
              width: 537,
              height: 453,
              top: 307,
              left: 76,
              background: 'rgba(164, 164, 164, 0.15)',
            }}
          >
            {/* Gradient border overlay */}
            <div 
              className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{
                background: 'linear-gradient(98.15deg, rgba(255,255,255,0.3) 62.94%, rgba(164,164,164,0.2) 99.21%)',
                padding: 1,
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            
            {/* Text Content - clickable categories */}
            <div 
              className="absolute font-inter font-semibold text-left"
              style={{
                width: 458,
                top: 26,
                left: 40,
                fontSize: 22,
                lineHeight: '79px',
                letterSpacing: '-0.05em',
                textTransform: 'capitalize',
                color: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              {FAQ_CATEGORIES.map((cat, index) => (
                <div
                  key={cat.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedCategoryIndex(index)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedCategoryIndex(index); } }}
                  className={`cursor-pointer rounded-lg px-4 -ml-4 hover:bg-white/5 ${selectedCategoryIndex === index ? 'bg-white/10' : ''}`}
                  style={{
                    display: 'block',
                    width: 'calc(100% + 32px)',
                  }}
                >
                  {cat.label}
                </div>
              ))}
            </div>
          </div>

          {/* Second FAQ Category Card - Purple */}
          <div 
            className="absolute rounded-2xl overflow-hidden"
            style={{
              width: 537,
              height: 376,
              top: 808,
              left: 76,
              background: '#533DDA',
            }}
          >
            {/* Gradient border overlay */}
            <div 
              className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{
                background: 'linear-gradient(98.15deg, rgba(255,255,255,0.3) 62.94%, rgba(164,164,164,0.2) 99.21%)',
                padding: 1,
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            
            {/* Card Content */}
            <div className="absolute" style={{ top: 56, left: 33 }}>
              {/* Headline - Still Have Questions? */}
              <h3 
                className="font-display font-semibold"
                style={{
                  width: 470,
                  height: 69,
                  fontSize: 40,
                  lineHeight: '57px',
                  letterSpacing: '-0.05em',
                  background: 'linear-gradient(90deg, #FFFFFF 0%, #999999 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Still Have Questions?
              </h3>
              
              {/* Body Text */}
              <p 
                className="absolute font-inter font-semibold"
                style={{
                  width: 453,
                  height: 155,
                  top: 64,
                  left: 4,
                  fontSize: 25,
                  lineHeight: '39px',
                  letterSpacing: '-0.05em',
                  textTransform: 'capitalize',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}
              >
                Our Team Is Here To Help. Contact Us By Phone, Email, Or Visit Our Support Page For Quick Answers.
              </p>
              
              {/* Contact Us Button */}
              <Link 
                to="/contact"
                className="absolute flex items-center justify-center gap-2 rounded-lg font-inter font-semibold"
                style={{
                  width: 182,
                  height: 49,
                  top: 217,
                  left: 4,
                  background: '#B0F18D',
                  color: '#000000',
                  fontSize: 18,
                  lineHeight: '34px',
                  letterSpacing: '-0.05em',
                  textTransform: 'capitalize',
                }}
              >
                <Phone size={20} />
                Contact Us
              </Link>
            </div>
          </div>

          {/* FAQ Answer Panel - Right Side */}
          <div 
            className="absolute rounded-2xl overflow-hidden"
            style={{
              width: 770,
              height: 877,
              top: 307,
              left: 654,
              background: 'rgba(164, 164, 164, 0.15)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: '0px 14px 84px 30px rgba(0, 0, 0, 0.15)',
            }}
          >
            {/* Noise texture overlay */}
            <div 
              className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{ opacity: 0.2 }}
            >
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <filter id="faqNoise">
                  <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="4" stitchTiles="stitch" />
                  <feColorMatrix type="saturate" values="0" />
                </filter>
                <rect width="100%" height="100%" filter="url(#faqNoise)" fill="#000000" />
              </svg>
            </div>
            
            {/* Gradient border overlay */}
            <div 
              className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{
                background: 'linear-gradient(98.15deg, rgba(255,255,255,0.3) 62.94%, rgba(164,164,164,0.2) 99.21%)',
                padding: 1,
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            
            {/* Panel Headline - dynamic per category */}
            <h3 
              className="absolute font-display font-semibold"
              style={{
                top: 54,
                left: 56,
                right: 56,
                fontSize: 54,
                lineHeight: '64px',
                letterSpacing: '-0.05em',
                background: 'linear-gradient(90deg, #FFFFFF 0%, #999999 68.68%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {activeCategory.headline}
            </h3>

            {/* FAQ Accordion */}
            <div 
              className="absolute"
              style={{
                width: 665,
                top: 200,
                left: 56,
              }}
            >
              <Accordion type="single" collapsible className="w-full">
                {faqItems.map((item, index) => (
                  <AccordionItem 
                    key={item.id} 
                    value={item.id}
                    className="border-b border-white/30 last:border-b-0"
                  >
                    <AccordionTrigger className="py-5 hover:no-underline group flex justify-between [&>svg]:hidden">
                      <span 
                        className="font-display font-semibold text-white text-left"
                        style={{
                          fontSize: 28,
                          lineHeight: '1.4',
                          letterSpacing: '-0.05em',
                        }}
                      >
                        {item.question}
                      </span>
                      <span className="shrink-0">
                        <ChevronDown 
                          className="h-10 w-10 transition-transform duration-200 group-data-[state=open]:rotate-180" 
                          style={{ color: '#B0F18D', opacity: 0.7 }}
                          strokeWidth={1.5}
                        />
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-white/70 font-inter text-lg leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
